import { useLayoutEffect, useRef, useState } from "react"
import { DotsSixVertical } from "@phosphor-icons/react"

import { NumericInput, PermissionTooltip } from "~/components/design-system/components"
import { usePermission } from "~/hooks/usePermission"
import { cn } from "~/utils/helpers"

const WEIGHT_SEGMENTS = [
  { color: "bg-button-primary", text: "text-fg-on-color" },
  { color: "bg-button-primary/60", text: "text-fg-on-color" },
  { color: "bg-badge-blue-bg", text: "text-badge-blue-fg-text" },
]

/** One period set to `value`; the others share the rest in their current proportion. */
function withWeight(weights: number[], index: number, value: number): number[] {
  const set = Math.min(100, Math.max(0, Math.round(value)))
  const rest = 100 - set
  const others = weights.map((_, other) => other).filter((other) => other !== index)
  const base = others.reduce((total, other) => total + weights[other]!, 0)
  const next = [...weights]
  next[index] = set
  for (const other of others) {
    next[other] =
      base > 0 ? Math.round((weights[other]! / base) * rest) : Math.round(rest / others.length)
  }
  next[others[0]!]! += 100 - next.reduce((total, weight) => total + weight, 0)
  return next
}

/** Running totals at each boundary between segments, so one fewer than the segments. */
function boundariesOf(weights: number[]): number[] {
  let total = 0
  return weights.slice(0, -1).map((weight) => (total += weight))
}

const SEGMENT_GAP_PX = 2
const SEGMENT_PADDING_PX = 24

/** Each segment is at least as wide as its label and input; only the room left over is shared
 * out by weight, so a weight of 0 still leaves the segment readable. Handles sit on the gaps. */
function barLayout(widestContentPx: number, segments: number) {
  const minPx = widestContentPx + SEGMENT_PADDING_PX
  const fixedPx = segments * minPx + (segments - 1) * SEGMENT_GAP_PX
  return {
    minPx,
    /** CSS left of the handle after segment `index`, at `cumulativePct` of the shared room. */
    handleLeft: (index: number, cumulativePct: number) =>
      `calc(${(index + 1) * minPx + index * SEGMENT_GAP_PX + SEGMENT_GAP_PX / 2}px + (100% - ${fixedPx}px) * ${cumulativePct / 100})`,
    /** The cumulative weight a pointer at `x` px into the bar puts on that handle. */
    pctAt: (index: number, x: number, barWidth: number) =>
      ((x - (index + 1) * minPx - index * SEGMENT_GAP_PX - SEGMENT_GAP_PX / 2) /
        Math.max(barWidth - fixedPx, 1)) *
      100,
  }
}

/** Weight bar with a draggable handle between each pair of segments and a typed percentage per
 * segment. One segment carries 100% and can't be edited. Drafts locally while dragging, commits
 * once on pointer-up. */
export function WeightsBar({
  weights,
  labels,
  disabled,
  onCommit,
}: {
  weights: number[]
  labels: string[]
  disabled: boolean
  onCommit: (weights: number[]) => void
}) {
  const { allowed: canEdit } = usePermission("edit")
  const barRef = useRef<HTMLDivElement>(null)
  const contentRefs = useRef<(HTMLDivElement | null)[]>([])
  const [widestContentPx, setWidestContentPx] = useState(150)
  const [draft, setDraft] = useState<number[] | null>(null)
  const pcts = draft ?? weights
  const boundaries = boundariesOf(pcts)
  const layout = barLayout(widestContentPx, pcts.length)

  const labelsKey = labels.join("|")
  useLayoutEffect(() => {
    const widths = contentRefs.current.map((content) => content?.offsetWidth ?? 0)
    setWidestContentPx(widths.reduce((widest, width) => Math.max(widest, width), 0))
  }, [labelsKey])

  const startDrag = (handle: number) => (event: React.PointerEvent) => {
    if (!canEdit || disabled) return
    event.preventDefault()
    const bar = barRef.current
    if (!bar) return
    const rect = bar.getBoundingClientRect()
    const toWeights = (clientX: number): number[] => {
      const pct = Math.round(layout.pctAt(handle, clientX - rect.left, rect.width))
      const moved = [...boundaries]
      moved[handle] = Math.min(Math.max(pct, moved[handle - 1] ?? 0), moved[handle + 1] ?? 100)
      return [...moved, 100].map((boundary, index) => boundary - (moved[index - 1] ?? 0))
    }
    let latest = [...pcts]
    const onMove = (e: PointerEvent) => {
      latest = toWeights(e.clientX)
      setDraft(latest)
    }
    const onUp = () => {
      window.removeEventListener("pointermove", onMove)
      window.removeEventListener("pointerup", onUp)
      setDraft(null)
      onCommit(latest)
    }
    window.addEventListener("pointermove", onMove)
    window.addEventListener("pointerup", onUp)
  }

  return (
    <div ref={barRef} className="relative flex h-10 w-full gap-0.5 select-none">
      {pcts.map((pct, index) => (
        <div
          key={index}
          className={cn(
            "flex min-w-0 items-center justify-center overflow-hidden first:rounded-l-lg last:rounded-r-lg",
            WEIGHT_SEGMENTS[index]!.color,
          )}
          style={{ flex: `${pct} 0 ${layout.minPx}px` }}
        >
          <div
            ref={(content) => {
              contentRefs.current[index] = content
            }}
            className="flex shrink-0 items-center gap-1.5"
          >
            <span
              className={cn(
                "paragraph-xs font-medium whitespace-nowrap",
                WEIGHT_SEGMENTS[index]!.text,
              )}
            >
              {labels[index]}
            </span>
            <NumericInput
              small
              requires="edit"
              disabled={disabled || pcts.length === 1}
              allowZero
              max={100}
              unit="%"
              aria-label={`${labels[index]} weight in percent`}
              value={pct}
              onChange={(value) => onCommit(withWeight(pcts, index, value))}
              containerClassName="w-24 shrink-0"
              className="w-full text-right tabular-nums"
            />
          </div>
        </div>
      ))}
      <div className="pointer-events-none absolute inset-0">
        {boundaries.map((cumulative, index) => (
          <PermissionTooltip
            key={index}
            requires="edit"
            allowedTitle="Drag to move weight between periods"
          >
            <div
              onPointerDown={startDrag(index)}
              className={cn(
                "pointer-events-auto absolute inset-y-0 z-10 -ml-2 flex w-4 items-center justify-center",
                canEdit && !disabled ? "cursor-ew-resize" : "cursor-not-allowed",
              )}
              style={{ left: layout.handleLeft(index, cumulative) }}
            >
              <div className="flex h-7.5 w-3.5 items-center justify-center rounded-md bg-base text-fg-muted shadow-card-rest">
                <DotsSixVertical size={12} />
              </div>
            </div>
          </PermissionTooltip>
        ))}
      </div>
    </div>
  )
}
